import { api } from './api';
import { LoginRequest, SignupRequest, AuthResponse, User } from '../types/auth';
import { storage, ACCESS_TOKEN_KEY } from '../utils/storage';

export const authService = {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await api.post('/auth/login', credentials);
    const data = response.data ?? {};
    const u = { ...data, ...(data.user || {}) };
    const mappedUser: User = {
      ...u,
      id: String(u.userId || u.id || ''),
      email: u.email || '',
      firstName: u.firstName || u.first_name || '',
      lastName: u.lastName || u.last_name || '',
      companyName: u.companyName || u.company_name || '',
      mobileNo: u.mobileNo || u.mobile_no || '',
      active: u.active, // This is explicitly in their response as 1
      roleId: u.roleId || u.role_id,
      roleName: u.roleName || u.role_name,
    };
    return {
      success: data.success === true || data.success === 'true',
      message: data.message ?? '',
      token: data.token ?? '',
      userId: Number(data.userId ?? u?.userId ?? u?.id) || 0,
      email: mappedUser.email,
      firstName: mappedUser.firstName,
      lastName: mappedUser.lastName,
      companyName: mappedUser.companyName,
      mobileNo: mappedUser.mobileNo,
      user: mappedUser,
    };
  },

  async signup(userData: SignupRequest): Promise<AuthResponse> {
    const signupPayload = {
      first_name: userData.firstName,
      last_name: userData.lastName,
      email: userData.email,
      password: userData.password,
      confirm_password: userData.confirmPassword,
      company_name: userData.companyName,
      brand_name: userData.brandName,
      mobile_no: userData.mobileNo,
      role: 'USER',
    };

    const response = await api.post('/auth/signup', signupPayload, {});

    // Map XML response to User object
    const xmlData = response.data;
    const mappedUser: User = {
      ...(xmlData || {}),
      id: xmlData.userId || '',
      email: xmlData.email || userData.email,
      firstName: xmlData.firstName || userData.firstName,
      lastName: xmlData.lastName || userData.lastName,
      companyName: xmlData.companyName || userData.companyName,
      mobileNo: xmlData.mobileNo || userData.mobileNo,
      brandName: userData.brandName || xmlData.brandName, // From request since not always in response
    };

    if (xmlData.token) {
      storage.set(ACCESS_TOKEN_KEY, xmlData.token);
      storage.set('USER_ID', mappedUser.id);
    }

    return {
      success: xmlData.success === true || xmlData.success === 'true',
      message: xmlData.message || '',
      token: xmlData.token || '',
      userId: parseInt(xmlData.userId) || 0,
      email: xmlData.email || userData.email,
      firstName: xmlData.firstName || userData.firstName,
      lastName: xmlData.lastName || userData.lastName,
      companyName: xmlData.companyName || userData.companyName,
      mobileNo: xmlData.mobileNo || userData.mobileNo,
      user: mappedUser,
    };
  },

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      storage.remove(ACCESS_TOKEN_KEY);
      storage.remove('USER_ID');
    }
  },

  async sendOtp(email: string): Promise<any> {
    const response = await api.post(
      `/auth/send-otp?email=${encodeURIComponent(email)}`
    );
    return response.data;
  },

  async verifyOtp(email: string, otp: string): Promise<any> {
    const response = await api.post(
      `/auth/verify-otp?email=${encodeURIComponent(email)}&otp=${encodeURIComponent(otp)}`
    );
    return response.data;
  },

  async forgotPassword(email: string): Promise<any> {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  async resetPassword(payload: {
    id: string;
    token: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<any> {
    const response = await api.post('/auth/reset-password', payload);
    return response.data;
  },
};
